import { useMemo } from "react";
import HomePage from "@/pages/HomePage";
import SalesPage from "@/pages/sales/SalesPage";
import HustlePlateSalesPage from "@/pages/HustlePlateSalesPage";
import HustleSuiteSalesPage from "@/pages/HustleSuiteSalesPage";
import PlazaSalesPage from "@/pages/PlazaSalesPage";
import PlazaDemoPage from "@/pages/PlazaDemoPage";
import HustleFinderPage from "@/pages/HustleFinderPage";
import TravelSalesPage from "@/pages/TravelSalesPage";
import DictateSalesPage from "@/pages/DictateSalesPage";
import HustleEventsSalesPage from "@/pages/HustleEventsSalesPage";
import HustleMeetupsOverviewPage from "@/pages/HustleMeetupsOverviewPage";
import HustleMeetLandingPage from "@/pages/HustleMeetLandingPage";
import HustleMeetCirclesSalesPage from "@/pages/HustleMeetCirclesSalesPage";
import HustleMeetMatchmakerSalesPage from "@/pages/HustleMeetMatchmakerSalesPage";
import HustleRoundupSalesPage from "@/pages/HustleRoundupSalesPage";
import DirectorySalesPage from "@/pages/DirectorySalesPage";
import HustleStadiumSalesPage from "@/pages/HustleStadiumSalesPage";
import HustleEatsSalesPage from "@/pages/HustleEatsSalesPage";
import HustleConnectSalesPage from "@/pages/HustleConnectSalesPage";
import HustleReviewsSalesPage from "@/pages/HustleReviewsSalesPage";
import HustleProfitMapsSalesPage from "@/pages/HustleProfitMapsSalesPage";
import HustleCRMSalesPage from "@/pages/HustleCRMSalesPage";
import HustlePressSalesPage from "@/pages/HustlePressSalesPage";
import HustleAnswerSalesPage from "@/pages/HustleAnswerSalesPage";
import HustleAnswerAppPage from "@/pages/hustleanswer/HustleAnswerAppPage";
import HustleAnswerDemoPage from "@/pages/hustleanswer/HustleAnswerDemoPage";
import TapNfcSalesPage from "@/pages/TapNfcSalesPage";
import TapRouterPage from "@/pages/tapnfc/TapRouterPage";
import RaroSiteRouter from "@/pages/raro/RaroSiteRouter";
import DirectoryPage from "@/pages/DirectoryPage";
import PwaLauncherPage from "@/pages/PwaLauncherPage";
import VendorsLandingPage from "@/pages/VendorsLandingPage";
import MembersLandingPage from "@/pages/MembersLandingPage";
// iter373c patch F (2026-09-22) — SiteRouter is the entry point for the
// branded whitelabel vendor site (Business Menu + service articles).
import SiteRouter from "@/pages/SiteRouter";
// iter262: hustlerewards.au bare-domain renders the same Rewards
// sub-sales page HustlePlate publishes at /rewards. Lazy — big page.
import { lazy } from "react";
const HustleRewardsSalesPage = lazy(() => import("@/pages/hustleplate/RewardsPage"));
// HustleShield Forms — AUD + USD sales pages rendered inline at `/`
// on hustleshield.au and emailspamshield.com respectively.
const HustleShieldSalesAUD = lazy(() => import("@/pages/forms/HustleShieldSalesAUD"));
const HustleShieldSalesUSD = lazy(() => import("@/pages/forms/HustleShieldSalesUSD"));
import DemoGate from "@/components/DemoGate";
import SiteLayout from "@/components/SiteLayout";
import { getPlazaMode } from "@/lib/plazaHost";
import { getModuleHost } from "@/lib/moduleHost";
import { resolveHostMode, resolveAppHostRoute } from "@/lib/hostRegistry";
import { Navigate } from "react-router-dom";

/** Marketing hosts that render the legacy thin SalesPage (mode "sales"). */
const MARKETING_HOSTS = new Set([
  "hustleplate.au",
  "www.hustleplate.au",
]);

/** Suite sales hosts — kept locally because `isSuiteSalesHost()` has a
 *  sessionStorage override (`?as=suite` / `?as=normal`) that the plain
 *  registry lookup doesn't model. */
const SUITE_SALES_HOSTS = new Set([
  "hustlesuite.au",
  "www.hustlesuite.au",
]);

const SUITE_OVERRIDE_KEY = "hs-suite-sales-override";

function isSuiteSalesHost() {
  const host = (window.location.hostname || "").toLowerCase();
  if (SUITE_SALES_HOSTS.has(host)) return true;
  try {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("as");
    if (q === "suite") { sessionStorage.setItem(SUITE_OVERRIDE_KEY, "1"); return true; }
    if (q === "normal") { sessionStorage.removeItem(SUITE_OVERRIDE_KEY); return false; }
    return sessionStorage.getItem(SUITE_OVERRIDE_KEY) === "1";
  } catch { return false; }
}

// Build-time flag: when REACT_APP_STANDALONE_SALES=1, the bundle ALWAYS
// renders the SalesPage at /, regardless of hostname. Used for the
// "drop on your own server" static export.
const STANDALONE_SALES = process.env.REACT_APP_STANDALONE_SALES === "1";

// Persistence key so once the user lands in sales mode (via ?sales=1 in
// the dev preview, or by visiting hustleplate.au), internal links keep
// the sales experience without re-stamping ?sales=1 on every href.
const SALES_MODE_KEY = "hp_sales_mode";

/**
 * Hostname-aware root route.
 *
 *  • hustleplate.au / www.hustleplate.au   → <SalesPage /> (standalone, no SiteLayout chrome)
 *  • hustleplaza.au / www.hustleplaza.au   → <HustlePlazaPage /> (public coming-soon storefront)
 *  • demo.hustleplaza.au                   → <PlazaDemoPage /> inside <DemoGate /> (gated playbook)
 *  • any other host (tenant restaurant)    → <HomePage /> wrapped in <SiteLayout />
 *  • ?sales=1 on any host                  → <SalesPage />  (preview escape hatch)
 *  • ?as=plaza|plaza-demo  preview override → simulate the plaza host
 */
export default function RootRouter() {
  const mode = useMemo(() => {
    if (STANDALONE_SALES) return "sales";
    if (typeof window === "undefined") return "home";
    // Preview override so agents/QA can smoke-test the travel & dictate
    // salespages without touching DNS. Persists in sessionStorage.
    try {
      const p = new URLSearchParams(window.location.search).get("as");
      if (p === "travel" || p === "dictate" || p === "travel-app" || p === "events") {
        sessionStorage.setItem("hs-root-override", p);
      } else if (p === "normal") {
        sessionStorage.removeItem("hs-root-override");
      }
      const ovr = sessionStorage.getItem("hs-root-override");
      if (ovr === "travel")     return "travel-sales";
      if (ovr === "dictate")    return "dictate-sales";
      if (ovr === "events")     return "events-sales";
      if (ovr === "travel-app") return "/roadtrip";
    } catch { /* ignore */ }
    // Strip `demo.` prefix for hostname-based routing so demo.<host>
    // renders the same landing page as <host> — DemoModeBanner (mounted
    // at the app root) handles the demo UI overlay + demo-login pill.
    const rawHost = (window.location.hostname || "").toLowerCase();
    const appHost = rawHost.startsWith("demo.") ? rawHost.slice(5) : rawHost;
    // 1. Direct-render subdomains (answer-app / answer-demo). Rendered
    //    inline via mode string so we skip the Navigate flash.
    const rawMode = resolveHostMode(rawHost);
    if (rawMode === "answer-app" || rawMode === "answer-demo" ||
        rawMode === "tapnfc-demo" || rawMode === "raro-site") return rawMode;
    // 2. `app.` / `admin.` Navigate-based routes (dashboards, plaza shop, etc.)
    const navPath = resolveAppHostRoute(appHost);
    if (navPath) return navPath;
    // 3. Bare-domain sales pages. Suite sales is handled below because
    //    it has a sessionStorage escape hatch.
    const appMode = resolveHostMode(appHost);
    if (appMode
        && appMode !== "suite-sales"
        && appMode !== "sales") return appMode;
    const plazaMode = getPlazaMode();
    if (plazaMode === "plaza-demo") return "plaza-demo";
    if (plazaMode === "plaza") return "plaza";
    const host = (window.location.hostname || "").toLowerCase();
    if (isSuiteSalesHost()) return "suite-sales";
    if (MARKETING_HOSTS.has(host)) return "sales";
    const moduleHost = getModuleHost();
    if (moduleHost && moduleHost.home !== "/") return moduleHost.home;
    const params = new URLSearchParams(window.location.search);
    if (params.get("sales") === "1") {
      try { sessionStorage.setItem(SALES_MODE_KEY, "1"); } catch (_e) { /* ignore */ }
      return "sales";
    }
    try {
      if (sessionStorage.getItem(SALES_MODE_KEY) === "1") return "sales";
    } catch (_e) { /* ignore */ }
    return "home";
  }, []);

  if (mode === "plaza-demo") {
    return (
      <DemoGate>
        <PlazaDemoPage />
      </DemoGate>
    );
  }
  // On the public hustleplaza.au host we now serve the multilingual
  // sales page. The full Shopify-style storefront lives at
  // app.hustleplaza.au (mapped in APP_HOST_ROUTES above).
  if (mode === "plaza") return <PlazaSalesPage />;
  if (mode === "finder") return <HustleFinderPage />;
  if (mode === "travel-sales") return <TravelSalesPage />;
  if (mode === "dictate-sales") return <DictateSalesPage />;
  if (mode === "events-sales") return <HustleEventsSalesPage />;
  if (mode === "meetups-overview") return <HustleMeetupsOverviewPage />;
  if (mode === "meet-landing")     return <HustleMeetLandingPage />;
  if (mode === "circles-sales")    return <HustleMeetCirclesSalesPage />;
  if (mode === "matchmaker-sales") return <HustleMeetMatchmakerSalesPage />;
  if (mode === "roundup-sales") return <HustleRoundupSalesPage />;
  if (mode === "rewards-sales") return <HustleRewardsSalesPage />;
  if (mode === "directory-sales") return <DirectorySalesPage />;
  // iter358: app.hustledirectory.au renders the full DirectoryPage at
  // root (`/`) — no /directory redirect, no login gate.
  if (mode === "directory-app")   return <DirectoryPage />;
  if (mode === "stadium-sales") return <HustleStadiumSalesPage />;
  if (mode === "eats-sales") return <HustleEatsSalesPage />;
  if (mode === "connect-sales") return <HustleConnectSalesPage />;
  if (mode === "reviews-sales") return <HustleReviewsSalesPage />;
  if (mode === "profitmaps-sales") return <HustleProfitMapsSalesPage />;
  if (mode === "crm-sales") return <HustleCRMSalesPage />;
  if (mode === "press-sales") return <HustlePressSalesPage />;
  if (mode === "shield-sales") return <HustleShieldSalesAUD />;
  if (mode === "shield-us-sales") return <HustleShieldSalesUSD />;
  if (mode === "answer-sales") return <HustleAnswerSalesPage />;
  if (mode === "answer-app")   return <HustleAnswerAppPage />;
  if (mode === "answer-demo")  return <HustleAnswerDemoPage />;
  if (mode === "tapnfc-sales") return <TapNfcSalesPage />;
  if (mode === "tapnfc-demo")  return <TapRouterPage forceTagId="demo" />;
  if (mode === "raro-site")    return <RaroSiteRouter />;
  if (mode === "pwa-launcher") return <PwaLauncherPage />;
  if (mode === "vendors-landing") return <VendorsLandingPage />;
  if (mode === "members-landing") return <MembersLandingPage />;
  // iter373c patch F (2026-09-22) — Whitelabel vendor sites. When the
  // hostname matches a whitelabel entry in hostRegistry, RootRouter
  // renders the branded SiteRouter inline at "/" with a hardcoded slug
  // so the browser URL stays clean (no /r/{slug} in the path). All
  // sub-paths (e.g. /pages/tax-savings) still hit the /r/:slug/*
  // Route in App.js untouched — this branch only claims the exact "/".
  if (typeof mode === "string" && mode.startsWith("whitelabel:")) {
    const forceSlug = mode.slice("whitelabel:".length);
    return <SiteRouter forceSlug={forceSlug} />;
  }
  if (mode === "suite-sales") return <HustleSuiteSalesPage />;
  // iter259: On hustleplate.au / www.hustleplate.au (MARKETING_HOSTS),
  // render the full-length HustlePlateSalesPage (2000+ word funnel with
  // Bigmenu → 9 sub-pages) instead of the legacy thin SalesPage. The old
  // <SalesPage /> path is preserved for ?sales=1 preview / standalone.
  if (mode === "sales") {
    const h = (typeof window !== "undefined" && window.location.hostname || "").toLowerCase();
    if (h === "hustleplate.au" || h === "www.hustleplate.au") return <HustlePlateSalesPage />;
    return <SalesPage />;
  }
  if (mode.startsWith("/")) return <Navigate to={mode} replace />;
  return (
    <SiteLayout>
      <HomePage />
    </SiteLayout>
  );
}
